import React, { useEffect, useMemo, useState } from "react";
import { Check, ExternalLink, LoaderCircle, Search, Sparkles } from "lucide-react";
import { apiFetch } from "../../commons/axiosReq";
import "./CollegeImporter.css";
import "./AcademicStructureImporter.css";

const normalizeName = (value) => (value || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();

export default function CollegeFieldImporter({ colleges = [], initialCollegeId = null, onChanged }) {
  const [collegeId, setCollegeId] = useState(initialCollegeId ? String(initialCollegeId) : "");
  const [maxFields, setMaxFields] = useState(50);
  const [suggestions, setSuggestions] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [unscopedSuggestions, setUnscopedSuggestions] = useState(false);

  useEffect(() => {
    setCollegeId(initialCollegeId ? String(initialCollegeId) : "");
    setSuggestions([]);
    setEdits({});
    setMessage("");
    setUnscopedSuggestions(false);
  }, [initialCollegeId]);

  const selectedCollege = useMemo(
    () => colleges.find((item) => String(item.value) === String(collegeId)),
    [colleges, collegeId]
  );

  const findFields = async () => {
    if (!selectedCollege?.universityId) {
      setMessageType("error");
      return setMessage("ابتدا دانشکده را انتخاب کنید.");
    }
    setBusy(true);
    setMessage("");
    setSuggestions([]);
    setUnscopedSuggestions(false);
    const [result, existingResult] = await Promise.all([
      apiFetch("AcademicStructureImport/OpenAlex/Preview", "post", {
        universityId: Number(selectedCollege.universityId),
        maxFields: Number(maxFields),
        collegeName: selectedCollege.label
      }),
      apiFetch("CollegeField/GetCollegeFieldsByCollege", "post", { collegeId: Number(collegeId) })
    ]);
    setBusy(false);
    if (!result.ok || !existingResult.ok) {
      setMessageType("error");
      return setMessage(result.error || existingResult.error || "جستجو یا بررسی رشته‌های موجود انجام نشد.");
    }
    const existingNames = new Set((Array.isArray(existingResult.data) ? existingResult.data : [])
      .map((item) => normalizeName(item.field?.name)));
    const selectedCollegeName = normalizeName(selectedCollege.label);
    const rawItems = Array.isArray(result.data) ? result.data : [];
    const unscoped = rawItems.length > 0 && !rawItems.some((item) => item.isCollegeMatched);
    const matchingField = rawItems.find((item) => normalizeName(item.fieldName) === selectedCollegeName);
    const selectedDomain = normalizeName(matchingField?.collegeSuggestion);
    const matchedItems = rawItems.filter((item) => {
      const domainName = normalizeName(item.collegeSuggestion);
      return domainName === selectedCollegeName || (selectedDomain && domainName === selectedDomain);
    });
    // Imported colleges are based on OpenAlex field names, while older or
    // manually renamed colleges may use domain/official names. If neither can
    // be inferred, keep the university-scoped results available for review.
    const items = (matchedItems.length ? matchedItems : rawItems)
      .filter((item) => !existingNames.has(normalizeName(item.fieldName)));
    setSuggestions(items);
    setUnscopedSuggestions(unscoped);
    setEdits(Object.fromEntries(items.map((item) => [item.openAlexFieldId, item.fieldName || ""])));
    setMessageType(unscoped ? "info" : items.length ? "success" : "info");
    setMessage(unscoped
      ? "نام این دانشکده با یک حوزهٔ واحد OpenAlex تطبیق نشد. پیشنهادهای پژوهشی کل دانشگاه نمایش داده شده‌اند؛ فقط رشته‌های مرتبط با ساختار رسمی این دانشکده را جداگانه بررسی و تأیید کنید."
      : items.length
        ? `${items.length.toLocaleString("fa-IR")} رشته پیشنهادی برای ${selectedCollege.label} پیدا شد.`
      : "همهٔ رشته‌های پیشنهادی این دانشکده از قبل ثبت شده‌اند.");
  };

  const importSuggestion = async (suggestion) => {
    const fieldName = (edits[suggestion.openAlexFieldId] || "").trim();
    if (!fieldName) {
      setMessageType("error");
      return setMessage("نام رشته را کامل کنید.");
    }
    setSavingId(suggestion.openAlexFieldId);
    setMessage("");
    const result = await apiFetch("AcademicStructureImport/OpenAlex/ApproveCollegeField", "post", {
      collegeId: Number(collegeId), fieldName, openAlexFieldId: suggestion.openAlexFieldId
    });
    setSavingId("");
    if (!result.ok) {
      setMessageType("error");
      return setMessage(result.error || "وارد کردن رشته انجام نشد.");
    }
    setSuggestions((current) => current.filter((item) => item.openAlexFieldId !== suggestion.openAlexFieldId));
    setMessageType("success");
    setMessage(result.data?.relationCreated ? "رشته با موفقیت به دانشکده اضافه شد." : "این رشته از قبل به دانشکده متصل بود.");
    if (onChanged) onChanged();
  };

  const importAllSuggestions = async () => {
    if (unscopedSuggestions) return;
    const invalidItems = suggestions.filter((item) => !(edits[item.openAlexFieldId] || "").trim());
    if (invalidItems.length) {
      setMessageType("error");
      return setMessage(`نام ${invalidItems.length.toLocaleString("fa-IR")} رشته خالی است؛ ابتدا آن‌ها را کامل کنید.`);
    }

    const importedIds = new Set();
    const errors = [];
    setSavingId("__all__");
    setBulkProgress({ done: 0, total: suggestions.length });
    setMessage("");

    // Keep concurrent writes bounded when a large maxFields value is used.
    for (let offset = 0; offset < suggestions.length; offset += 5) {
      const batch = suggestions.slice(offset, offset + 5);
      const results = await Promise.all(batch.map(async (suggestion) => ({
        suggestion,
        result: await apiFetch("AcademicStructureImport/OpenAlex/ApproveCollegeField", "post", {
          collegeId: Number(collegeId),
          fieldName: edits[suggestion.openAlexFieldId].trim(),
          openAlexFieldId: suggestion.openAlexFieldId
        })
      })));
      results.forEach(({ suggestion, result }) => {
        if (result.ok) importedIds.add(suggestion.openAlexFieldId);
        else errors.push(result.error || `ورود ${edits[suggestion.openAlexFieldId]} انجام نشد.`);
      });
      setBulkProgress({ done: Math.min(offset + batch.length, suggestions.length), total: suggestions.length });
    }

    setSuggestions((current) => current.filter((item) => !importedIds.has(item.openAlexFieldId)));
    setSavingId("");
    setBulkProgress(null);
    if (importedIds.size && onChanged) onChanged();

    if (!errors.length) {
      setMessageType("success");
      setMessage(`${importedIds.size.toLocaleString("fa-IR")} رشته با موفقیت تأیید و وارد شد.`);
    } else {
      setMessageType("error");
      setMessage(`${importedIds.size.toLocaleString("fa-IR")} رشته وارد شد و ${errors.length.toLocaleString("fa-IR")} مورد انجام نشد. ${errors[0]}`);
    }
  };

  return <div className="college-importer">
    <div className="college-importer-intro"><span><Sparkles size={20} /></span><div><strong>پیدا کردن رشته‌ها با OpenAlex</strong><p>رشته‌های مرتبط با حوزهٔ پژوهشی دانشکده نمایش داده می‌شوند؛ پیشنهادها را پیش از ورود بررسی کنید.</p></div></div>
    <div className="college-importer-controls">
      <label>دانشکده<select value={collegeId} disabled={Boolean(initialCollegeId) || busy || Boolean(savingId)} onChange={(event) => { setCollegeId(event.target.value); setSuggestions([]); setMessage(""); setUnscopedSuggestions(false); }}><option value="">انتخاب دانشکده</option>{colleges.map((item) => <option key={item.value} value={item.value}>{item.label}{item.universityName ? ` · ${item.universityName}` : ""}</option>)}</select></label>
      <label>تعداد پیشنهاد<select value={maxFields} disabled={busy || Boolean(savingId)} onChange={(event) => setMaxFields(Number(event.target.value))}><option value={12}>۱۲ مورد</option><option value={30}>۳۰ مورد</option><option value={50}>۵۰ مورد</option><option value={100}>۱۰۰ مورد</option><option value={200}>۲۰۰ مورد</option></select></label>
      <button type="button" disabled={busy || Boolean(savingId) || !selectedCollege?.universityId} onClick={findFields}>{busy ? <LoaderCircle className="college-importer-spinner" size={18} /> : <Search size={18} />}{busy ? "در حال جستجو..." : "پیدا کردن رشته‌ها"}</button>
    </div>
    {message && <div className={`college-importer-message is-${messageType}`}>{message}</div>}
    {suggestions.length > 0 && <div className="college-importer-results">
      <div className="college-importer-results-head"><strong>رشته‌های پیشنهادی</strong><div className="college-importer-results-head-actions"><span>{suggestions.length.toLocaleString("fa-IR")} مورد باقی مانده</span><button type="button" disabled={Boolean(savingId) || unscopedSuggestions} onClick={importAllSuggestions}>{savingId === "__all__" ? <LoaderCircle className="college-importer-spinner" size={16} /> : <Check size={16} />}{bulkProgress ? `در حال ورود ${bulkProgress.done.toLocaleString("fa-IR")} از ${bulkProgress.total.toLocaleString("fa-IR")}` : "تأیید و ورود همه"}</button></div></div>
      <div className="college-importer-list">{suggestions.map((suggestion) => {
        const saving = savingId === suggestion.openAlexFieldId;
        return <article key={suggestion.openAlexFieldId} className="college-importer-card is-single-field">
          <div className="college-importer-card-meta"><span>{Number(suggestion.worksCount || 0).toLocaleString("fa-IR")} اثر پژوهشی</span>{suggestion.sourceUrl && <a href={suggestion.sourceUrl} target="_blank" rel="noreferrer">منبع <ExternalLink size={13} /></a>}</div>
          <label>نام رشته<input disabled={Boolean(savingId)} value={edits[suggestion.openAlexFieldId] || ""} onChange={(event) => setEdits((current) => ({ ...current, [suggestion.openAlexFieldId]: event.target.value }))} /></label>
          <button type="button" disabled={saving || Boolean(savingId)} onClick={() => importSuggestion(suggestion)}>{saving ? <LoaderCircle className="college-importer-spinner" size={17} /> : <Check size={17} />}{saving ? "در حال ورود..." : "تأیید و ورود"}</button>
        </article>;
      })}</div>
    </div>}
  </div>;
}
